import { plural, t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { z } from "zod"
import type { CatalogueArticle } from "@/features/bill-of-materials/core/articleCatalogue"
import { articleCatalogueQueryOptions } from "@/features/bill-of-materials/hooks/useArticleCatalogue"
import { deleteLomArticleFn } from "@/features/bill-of-materials/server/articleCatalogue"
import { AddMissingPanel } from "@/features/list-of-materials/components/articles/AddMissingPanel"
import {
  ArticleFormPanel,
  type ArticleFormTarget,
} from "@/features/list-of-materials/components/articles/ArticleFormPanel"
import { ArticleGaps } from "@/features/list-of-materials/components/articles/ArticleGaps"
import { ArticleImportPanel } from "@/features/list-of-materials/components/articles/ArticleImportPanel"
import { ArticleListTable } from "@/features/list-of-materials/components/articles/ArticleListTable"
import { FillInWorkbook } from "@/features/list-of-materials/components/fill-in/FillInWorkbook"
import { ConfirmDialog } from "@/features/list-of-materials/components/form/ConfirmDialog"
import { ExportExcelButton } from "@/features/list-of-materials/components/form/ExportExcelButton"
import { HowLink } from "@/features/list-of-materials/components/how-it-works/HowLink"
import { Block } from "@/features/list-of-materials/components/ui/Block"
import { Button } from "@/features/list-of-materials/components/ui/Button"
import { Chip } from "@/features/list-of-materials/components/ui/Chip"
import { FilterBar } from "@/features/list-of-materials/components/ui/FilterBar"
import { PageHead, PageStatus } from "@/features/list-of-materials/components/ui/PageHead"
import { SearchField } from "@/features/list-of-materials/components/ui/SearchField"
import { TabLink, Tabs } from "@/features/list-of-materials/components/ui/Tabs"
import { listArticles } from "@/features/list-of-materials/core/articleList"
import { formatCount } from "@/features/list-of-materials/core/format"
import { useCanEditLom } from "@/features/list-of-materials/hooks/useCanEditLom"
import { useLomMutation } from "@/features/list-of-materials/hooks/useLomMutation"
import {
  lomVersionsQueryOptions,
  useSelectedLomVersion,
} from "@/features/list-of-materials/hooks/useLomVersion"
import { exportArticleListFn } from "@/features/list-of-materials/server/lomArticles"

export const Route = createFileRoute("/(authenticated)/list-of-materials/articles")({
  validateSearch: z.object({ view: z.enum(["all", "fill-in"]).optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context, deps }) => {
    const { selected } = await context.queryClient.ensureQueryData(
      lomVersionsQueryOptions(deps.version),
    )
    if (!selected) return
    await context.queryClient.ensureQueryData(articleCatalogueQueryOptions(selected.id))
  },
  component: ArticlesPage,
})

function ArticlesPage() {
  const { version, versionSearch } = useSelectedLomVersion()
  const { view = "all" } = Route.useSearch()
  const canEdit = useCanEditLom()
  const { data } = useSuspenseQuery(articleCatalogueQueryOptions(version.id))
  const [query, setQuery] = useState("")
  const [editing, setEditing] = useState<ArticleFormTarget | null>(null)
  const [removing, setRemoving] = useState<CatalogueArticle | null>(null)
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)

  const inUse = useMemo(() => new Map(data.inUse.map((a) => [a.articleNumber, a])), [data.inUse])
  const openNumbers = useMemo(
    () =>
      new Set([...data.gaps.creme.open, ...data.gaps.extraPerRun.open].map((a) => a.articleNumber)),
    [data.gaps],
  )
  const toFillIn = data.gaps.creme.open.length + data.gaps.extraPerRun.open.length
  const open = toFillIn + data.missing.length
  const shown = useMemo(
    () => listArticles(data.articles, openNumbers, query),
    [data.articles, openNumbers, query],
  )

  const remove = useLomMutation(
    (article: CatalogueArticle) => deleteLomArticleFn({ data: { id: article.id } }),
    { success: t`Removed the article from the list`, onSuccess: () => setRemoving(null) },
  )

  return (
    <>
      <PageHead
        help={<HowLink topic="articles" />}
        title={<Trans>Articles</Trans>}
        sub={
          <Trans>
            Every article the list of materials can order, with what a creme (1.0) store orders
            instead and whether a run needs one extra. Shelf codes, rules and extras pick their
            articles from this list.
          </Trans>
        }
        right={
          <PageStatus>
            {open > 0 ? (
              <Chip tone="fill" dot>
                {t`${open} to fill in`}
              </Chip>
            ) : (
              <Chip tone="ok" dot>
                <Trans>Complete</Trans>
              </Chip>
            )}
            {t`${formatCount(data.articles.length)} articles`}
            <ExportExcelButton
              exportFile={() => exportArticleListFn({ data: { configVersionId: version.id } })}
            />
            {canEdit && (
              <Button tone="secondary" onClick={() => setImporting(true)}>
                <Trans>Import Excel</Trans>
              </Button>
            )}
            {canEdit && (
              <Button tone="primary" onClick={() => setEditing({})}>
                <Trans>New article</Trans>
              </Button>
            )}
          </PageStatus>
        }
      />

      {data.missing.length > 0 && (
        <Block className="border-l-lom-warn mb-6 flex flex-wrap items-center gap-4 border-l-2 px-4 py-3">
          <span className="text-[15px]">
            {plural(data.missing.length, {
              one: "# article is used in this version but isn't in the list yet.",
              other: "# articles are used in this version but aren't in the list yet.",
            })}
          </span>
          {canEdit && (
            <Button tone="secondary" small onClick={() => setAdding(true)} className="ml-auto">
              <Trans>Add them</Trans>
            </Button>
          )}
        </Block>
      )}

      <Tabs>
        <TabLink
          to="/list-of-materials/articles"
          search={versionSearch}
          current={view === "all"}
          count={data.articles.length}
        >
          <Trans>All</Trans>
        </TabLink>
        <TabLink
          to="/list-of-materials/articles"
          search={{ ...versionSearch, view: "fill-in" }}
          current={view === "fill-in"}
          count={toFillIn}
        >
          <Trans>To fill in</Trans>
        </TabLink>
      </Tabs>

      {view === "fill-in" ? (
        <>
          <FillInWorkbook configVersionId={version.id} canEdit={canEdit} />
          <ArticleGaps
            gaps={data.gaps}
            inUse={inUse}
            cremeStoreCount={data.cremeStoreCount}
            canEdit={canEdit}
          />
        </>
      ) : (
        <>
          <FilterBar count={t`${shown.length} shown`}>
            <SearchField
              className="w-96 max-w-full"
              value={query}
              onChange={setQuery}
              placeholder={t`Search number or description, white or creme`}
              label={t`Search articles`}
            />
          </FilterBar>
          <ArticleListTable
            articles={shown}
            catalogue={data.articles}
            inUse={inUse}
            canEdit={canEdit}
            onEdit={(article) => setEditing({ article })}
            onDelete={setRemoving}
          />
        </>
      )}

      <ArticleFormPanel
        target={editing}
        onClose={() => setEditing(null)}
        configVersionId={version.id}
        listed={data.articles.map((a) => a.articleNumber)}
      />
      <AddMissingPanel
        open={adding}
        onClose={() => setAdding(false)}
        configVersionId={version.id}
        missing={data.missing}
      />
      <ArticleImportPanel
        open={importing}
        onClose={() => setImporting(false)}
        configVersionId={version.id}
      />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t`Remove ${removing?.articleNumber ?? ""} from the article list?`}
        confirmLabel={t`Remove`}
        pending={remove.isPending}
        error={remove.error}
        onConfirm={() => removing && remove.mutate(removing)}
      >
        <p>
          {inUse.has(removing?.articleNumber ?? "")
            ? t`The version still uses it, so it shows up again as missing from the list. Its creme setting and extra per run are lost.`
            : t`The version doesn't use it anywhere.`}
        </p>
      </ConfirmDialog>
    </>
  )
}
